import React, { useEffect, useId, useRef, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
} from '@mui/material'
import styles from './CommandActionsModal.module.scss'
import CommandHistory from '../CommandHistory/CommandHistory'
import { useDeviceCatalog } from '../../hooks/useDeviceCatalog'
import { useSendCommand } from '../../hooks/useSendCommand'
import { CommandSpec } from '../../types/commandBus.types'
import { coerceParamValue, validateParam } from '../../utils/params'
import {
  DeviceNotProvisionedError,
  MomiOfflineError,
} from '../../service/commandBusAPI'
import { useToast } from '../../../../common/providers/ToastProvider/useToast'
import { PermissionTooltip, capabilityForCommand, useCapability } from '../../../rbac'
import {
  CategoryId,
  getLocale,
  commandLabel,
  categoryLabel,
  uiStrings,
} from '../../i18n/commandBusI18n'

// ── types ──────────────────────────────────────────────────────────────────

export interface CommandActionsModalProps {
  isOpen: boolean
  onClose: () => void
  devEui: string
}

type TabId = 'commands' | 'history'

// ── category helpers (labels come from the i18n module) ─────────────────────

interface Category {
  id: CategoryId
  icon: string
}

// Empty groups are skipped at render time, so a device only shows the
// categories its catalog actually uses (meters: relais/lecture/écriture,
// luminaires: contrôle/configuration/maintenance).
const CATEGORIES: Category[] = [
  { id: 'relais',        icon: '⚡' },
  { id: 'controle',      icon: '🎛️' },
  { id: 'lecture',       icon: '📖' },
  { id: 'ecriture',      icon: '✏️' },
  { id: 'configuration', icon: '🛠️' },
  { id: 'maintenance',   icon: '🔧' },
  { id: 'autres',        icon: '⚙️' },
]

const CATEGORY_IDS = new Set<string>(CATEGORIES.map((c) => c.id))

const RELAY_NAMES = new Set(['open_relay', 'close_relay', 'read_relay'])
const ECRITURE_NAMES = new Set(['send_token', 'send_at_command', 'time_sync'])
// Commandes de contrôle luminaire (UL20xx) sans catégorie explicite : on les
// classe en "controle" plutôt que de les laisser tomber dans "autres".
const CONTROL_NAMES = new Set(['on', 'off', 'dimming', 'dim', 'dim_resume'])

// "Contrôle" -> "controle", "Écriture" -> "ecriture", ...
const normalizeCategory = (raw: string): string =>
  raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

function getCategory(cmd: CommandSpec): CategoryId {
  // The momi catalog ships an explicit operator-sheet category since 06/2026;
  // prefer it. The name-based heuristic below stays as the fallback for
  // catalogs pushed by older momis (no category field).
  if (cmd.category) {
    const id = normalizeCategory(cmd.category)
    if (CATEGORY_IDS.has(id)) return id as CategoryId
  }
  if (RELAY_NAMES.has(cmd.name)) return 'relais'
  if (CONTROL_NAMES.has(cmd.name)) return 'controle'
  if (cmd.name.startsWith('read_')) return 'lecture'
  if (cmd.name.startsWith('write_') || ECRITURE_NAMES.has(cmd.name)) return 'ecriture'
  return 'autres'
}

function groupByCategory(
  commands: CommandSpec[],
): Map<CategoryId, CommandSpec[]> {
  const map = new Map<CategoryId, CommandSpec[]>()
  for (const cat of CATEGORIES) map.set(cat.id, [])
  for (const cmd of commands) {
    map.get(getCategory(cmd))!.push(cmd)
  }
  return map
}

// param coercion + validation vivent dans ../../utils/params (partagées).

// ── inline param form ──────────────────────────────────────────────────────

interface InlineParamFormProps {
  command: CommandSpec
  momiOnline: boolean
  isSubmitting: boolean
  onCancel: () => void
  onSubmit: (params: Record<string, unknown>) => void
}

const InlineParamForm: React.FC<InlineParamFormProps> = ({
  command,
  momiOnline,
  isSubmitting,
  onCancel,
  onSubmit,
}) => {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {}
    command.params.forEach((p) => {
      init[p.name] = p.default ?? (p.type === 'boolean' ? 'false' : '')
    })
    return init
  })
  const [errors, setErrors] = useState<Record<string, string | null>>({})
  const [confirmOpen, setConfirmOpen] = useState(false)

  const locale = getLocale()
  const ui = uiStrings(locale)
  const cmdLabel = commandLabel(command.name, locale, command.label)

  const handleChange = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }))
    setErrors((prev) => ({ ...prev, [name]: null }))
  }

  const runSubmit = () => {
    const nextErrors: Record<string, string | null> = {}
    let hasError = false
    command.params.forEach((p) => {
      const err = validateParam(values[p.name] ?? '', p)
      nextErrors[p.name] = err
      if (err) hasError = true
    })
    setErrors(nextErrors)
    if (hasError) return

    const coerced: Record<string, unknown> = {}
    command.params.forEach((p) => {
      const raw = values[p.name] ?? ''
      if (!p.required && raw.trim() === '' && p.type !== 'boolean') return
      coerced[p.name] = coerceParamValue(raw, p)
    })
    onSubmit(coerced)
  }

  const handleEnvoyer = () => {
    if (command.destructive) {
      setConfirmOpen(true)
    } else {
      runSubmit()
    }
  }

  const disabled = !momiOnline || isSubmitting

  return (
    <>
      <div className={styles.paramForm}>
        {command.params.map((p) => (
          <div key={p.name} className={styles.paramRow}>
            <label
              className={styles.paramLabel}
              htmlFor={`modal-${command.name}-${p.name}`}
            >
              {p.name}
              {p.required ? ' *' : ''}
              {p.validation ? ` (${p.validation})` : ''}
            </label>
            {p.type === 'boolean' ? (
              <div className={styles.paramCheckbox}>
                <input
                  id={`modal-${command.name}-${p.name}`}
                  type="checkbox"
                  checked={values[p.name] === 'true'}
                  disabled={disabled}
                  onChange={(e) =>
                    handleChange(p.name, e.target.checked ? 'true' : 'false')
                  }
                />
                <span>{values[p.name] === 'true' ? 'true' : 'false'}</span>
              </div>
            ) : (
              <input
                id={`modal-${command.name}-${p.name}`}
                className={styles.paramInput}
                type={p.type === 'integer' ? 'number' : 'text'}
                value={values[p.name] ?? ''}
                disabled={disabled}
                placeholder={p.default ?? ''}
                onChange={(e) => handleChange(p.name, e.target.value)}
              />
            )}
            {errors[p.name] && (
              <span className={styles.paramError}>{errors[p.name]}</span>
            )}
          </div>
        ))}

        <div className={styles.paramFormActions}>
          <button type="button" className={styles.cancelBtn} onClick={onCancel}>
            {ui.cancel}
          </button>
          <button
            type="button"
            className={[
              styles.submitBtn,
              command.destructive ? styles.submitBtnDestructive : '',
            ].join(' ')}
            onClick={handleEnvoyer}
            disabled={disabled}
          >
            {isSubmitting ? ui.sending : ui.send}
          </button>
        </div>
      </div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{ui.confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{ui.confirmBody(cmdLabel)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmOpen(false)}>{ui.cancel}</MuiButton>
          <MuiButton
            onClick={() => {
              setConfirmOpen(false)
              runSubmit()
            }}
            color="error"
            variant="contained"
          >
            {ui.confirm}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

// ── single command row ─────────────────────────────────────────────────────

interface CommandRowProps {
  command: CommandSpec
  icon: string
  momiOnline: boolean
  // RBAC : capacité propre à la commande (capabilityForCommand) : `relay.read`
  // pour la liste explicite de lectures, `relay.command` sinon.
  canSend: boolean
  isSubmitting: boolean
  onSend: (cmd: CommandSpec, params: Record<string, unknown>) => void
}

const CommandRow: React.FC<CommandRowProps> = ({
  command,
  icon,
  momiOnline,
  canSend,
  isSubmitting,
  onSend,
}) => {
  const [expanded, setExpanded] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  // Garde anti double-envoi : `isSubmitting` (React Query) ne bascule pas de
  // façon synchrone, donc deux clics rapides sur une commande sans paramètre
  // pourraient déclencher deux envois avant le re-render. Le ref bloque le 2e
  // tant que le 1er n'est pas résolu (reset quand isSubmitting repasse à false).
  const inFlightRef = useRef(false)

  const locale = getLocale()
  const ui = uiStrings(locale)
  const cmdLabel = commandLabel(command.name, locale, command.label)

  const hasParams = command.params.length > 0
  const disabled = !canSend || !momiOnline || isSubmitting

  useEffect(() => {
    if (!isSubmitting) inFlightRef.current = false
  }, [isSubmitting])

  const handleRowClick = () => {
    if (disabled) return
    if (hasParams) {
      setExpanded((v) => !v)
    } else if (command.destructive) {
      setConfirmOpen(true)
    } else {
      if (inFlightRef.current) return
      inFlightRef.current = true
      onSend(command, {})
    }
  }

  return (
    <>
      <PermissionTooltip allowed={canSend} fullWidth>
      <button
        type="button"
        className={[
          styles.commandItem,
          command.destructive ? styles.commandItemDestructive : '',
        ].join(' ')}
        onClick={handleRowClick}
        disabled={disabled}
        aria-expanded={hasParams ? expanded : undefined}
      >
        <span className={styles.commandItemIcon}>{icon}</span>
        <span className={styles.commandItemText}>
          <span className={styles.commandItemTitle}>
            {cmdLabel}
            {command.destructive && (
              <span className={styles.destructiveTag}>{ui.destructiveTag}</span>
            )}
          </span>
          <span className={styles.commandItemDescription}>{command.name}</span>
        </span>
        <span className={styles.commandItemArrow}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={
              hasParams && expanded
                ? { transform: 'rotate(90deg)', transition: 'transform 0.2s ease' }
                : { transition: 'transform 0.2s ease' }
            }
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      </PermissionTooltip>

      {/* Parameterized form, shown inline below the row */}
      {hasParams && expanded && canSend && (
        <InlineParamForm
          command={command}
          momiOnline={momiOnline}
          isSubmitting={isSubmitting}
          onCancel={() => setExpanded(false)}
          onSubmit={(params) => {
            setExpanded(false)
            onSend(command, params)
          }}
        />
      )}

      {/* Destructive confirm for parameterless commands */}
      {!hasParams && (
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>{ui.confirmTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText>{ui.confirmBody(cmdLabel)}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={() => setConfirmOpen(false)}>{ui.cancel}</MuiButton>
            <MuiButton
              onClick={() => {
                setConfirmOpen(false)
                onSend(command, {})
              }}
              color="error"
              variant="contained"
            >
              {ui.confirm}
            </MuiButton>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

// ── commands tab ───────────────────────────────────────────────────────────

interface CommandsTabProps {
  devEui: string
}

const CommandsTab: React.FC<CommandsTabProps> = ({ devEui }) => {
  const catalogQuery = useDeviceCatalog(devEui)
  const sendMutation = useSendCommand()
  // RBAC par commande : le lecteur n'envoie que les lectures de la liste
  // explicite (relay.read) ; tout le reste exige relay.command (opérateur).
  const canRead = useCapability('relay.read')
  const canCommand = useCapability('relay.command')
  const canSendCommand = (name: string) =>
    capabilityForCommand(name) === 'relay.read' ? canRead : canCommand
  const toast = useToast()
  const locale = getLocale()
  const ui = uiStrings(locale)

  const handleSend = (cmd: CommandSpec, params: Record<string, unknown>) => {
    if (!canSendCommand(cmd.name)) return
    sendMutation.mutate(
      { devEui, commandName: cmd.name, params },
      {
        onSuccess: (res) => {
          const suffix = res.chirpstack_queue_id
            ? ` (ChirpStack: ${res.chirpstack_queue_id})`
            : ''
          toast.success(`Commande envoyée — ${res.status}${suffix}`)
        },
        onError: (err) => {
          if (err instanceof MomiOfflineError) {
            toast.error(ui.momiOffline)
            return
          }
          const msg =
            (err as any)?.response?.data?.error ??
            (err as any)?.message ??
            "Erreur lors de l'envoi de la commande"
          toast.error(typeof msg === 'string' ? msg : "Erreur lors de l'envoi")
        },
      },
    )
  }

  if (catalogQuery.error instanceof DeviceNotProvisionedError) {
    return <div className={styles.stateBox}>{ui.notProvisioned}</div>
  }

  if (catalogQuery.isLoading) {
    return <div className={styles.stateBox}>{ui.loadingCatalog}</div>
  }

  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <div className={[styles.stateBox, styles.stateError].join(' ')}>
        {ui.catalogError}
      </div>
    )
  }

  const catalog = catalogQuery.data
  const momiOnline = catalog.momi_online
  const grouped = groupByCategory(catalog.commands)

  if (catalog.commands.length === 0) {
    return <div className={styles.stateBox}>{ui.noCommands}</div>
  }

  return (
    <>
      {CATEGORIES.map((cat) => {
        const cmds = grouped.get(cat.id) ?? []
        if (cmds.length === 0) return null
        return (
          <div key={cat.id} className={styles.categorySection}>
            <p className={styles.categoryLabel}>{categoryLabel(cat.id, locale)}</p>
            {cmds.map((cmd) => (
              <CommandRow
                key={cmd.name}
                command={cmd}
                icon={cat.icon}
                momiOnline={momiOnline}
                canSend={canSendCommand(cmd.name)}
                isSubmitting={sendMutation.isLoading}
                onSend={handleSend}
              />
            ))}
          </div>
        )
      })}
    </>
  )
}

// ── header status badge (derived from catalog if loaded) ───────────────────

interface HeaderBadgeProps {
  devEui: string
}

const HeaderBadge: React.FC<HeaderBadgeProps> = ({ devEui }) => {
  const catalogQuery = useDeviceCatalog(devEui)
  const ui = uiStrings(getLocale())

  if (!catalogQuery.data) return null

  return catalogQuery.data.momi_online ? (
    <span className={styles.onlineBadge}>
      <span className={[styles.dot, styles.dotOnline].join(' ')} />
      {ui.momiOnline}
    </span>
  ) : (
    <span className={styles.offlineBadge}>
      <span className={[styles.dot, styles.dotOffline].join(' ')} />
      {ui.momiOffline}
    </span>
  )
}

// ── main modal ─────────────────────────────────────────────────────────────

export default function CommandActionsModal({
  isOpen,
  onClose,
  devEui,
}: CommandActionsModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('commands')
  const catalogQuery = useDeviceCatalog(devEui)
  const ui = uiStrings(getLocale())
  const titleId = useId()

  // Fermeture au clavier : Escape ferme la modale (avec cleanup du listener).
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const subtitle = catalogQuery.data
    ? `${catalogQuery.data.device_family} · MoMi ${catalogQuery.data.momi_id}`
    : undefined

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modalContent}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <h2 id={titleId} className={styles.modalTitle}>
              Actions — {devEui}
            </h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          <div className={styles.headerRight}>
            <HeaderBadge devEui={devEui} />
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className={styles.tabBar} role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'commands'}
            className={[
              styles.tab,
              activeTab === 'commands' ? styles.tabActive : '',
            ].join(' ')}
            onClick={() => setActiveTab('commands')}
          >
            {ui.tabCommands}
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'history'}
            className={[
              styles.tab,
              activeTab === 'history' ? styles.tabActive : '',
            ].join(' ')}
            onClick={() => setActiveTab('history')}
          >
            {ui.tabHistory}
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {activeTab === 'commands' && <CommandsTab devEui={devEui} />}
          {activeTab === 'history' && <CommandHistory devEui={devEui} />}
        </div>
      </div>
    </div>
  )
}
